import {store as folioStore} from "../store/FolioStore.js"
import {injectCssVars} from "@/scripts/common/utilities/cssVars.js";
import {
    getBestColor,
    mixColors,
    resolveNonHexColor,
    setAutoColorForSectionForegroundItems
} from "@/scripts/common/utilities/colorUtils.js";
import {
    HeroLayoutTypeEnum,
    NavigationSidebarPlacementEnum,
    SectionTypesEnum
} from "@/scripts/common/enums.js";
import {resolveColor} from "@/scripts/common/helpers.js";

const portfolioHeroTextStyleKeysAndVarNames = [
    { key: 'heroTitle', varName: '--portfolio-hero-h1'  },
    { key: 'heroSubtitle', varName: '--portfolio-hero-h2' },
    { key: 'navigation', varName: '--portfolio-hero-nav-button' },
    { key: 'heroParagraph', varName: '--portfolio-hero-p1' },
];

const textStyleKeysAndVarNames = [
    { key: 'heroTitle', varName: '--hero-h1' },
    { key: 'heroSubtitle', varName: '--hero-h2' },
    { key: 'navigation', varName: '--hero-nav-button' },
    { key: 'heroParagraph', varName: '--hero-p1' },
    { key: 'h1', varName: '--section-h1',  },
    { key: 'h2', varName: '--section-h2' },
    { key: 'h3', varName: '--section-h3' },
    { key: 'h4', varName: '--section-h4' },
    { key: 'p1', varName: '--section-p1' },
    { key: 'p2', varName: '--section-p2' },
    { key: 'projectTitle', varName: '--thumbnail-title' },
    { key: 'projectSubtitle', varName: '--thumbnail-subtitle' },
    { key: 'button', varName: '--cta-button' },
    { key: 'secondaryButton', varName: '--cta-secondary-button' },
    { key: 'label', varName: '--label' },
    { key: 'pageTitle', varName: '--page-title' },
];

const colorKeysAndLabels = [
    { key: 'heroTitle', varName: '--hero-h1' },
    { key: 'heroSubtitle', varName: '--hero-h2' },
    { key: 'heroParagraph', varName: '--hero-p1' },
    { key: 'h1', varName: '--section-h1' },
    { key: 'h2', varName: '--section-h2' },
    { key: 'h3', varName: '--section-h3' },
    { key: 'h4', varName: '--section-h4' },
    { key: 'p1', varName: '--section-p1' },
    { key: 'p2', varName: '--section-p2' },
    { key: 'projectTitle', varName: '--thumbnail-title' },
    { key: 'projectSubtitle', varName: '--thumbnail-subtitle' },
    { key: 'background', varName: '--background' },
    { key: 'navigation', varName: '--hero-nav-button' },
    { key: 'hyperLink', varName: '--link' },
    { key: 'dividerLine', varName: '--divider-line' },
    { key: 'button', varName: '--cta-button' },
    { key: 'secondaryButton', varName: '--cta-secondary-button' },
    { key: 'label', varName: '--label' },
    { key: 'sidebarBackground', varName: '--sidebar-background' },
    { key: 'card', varName: '--card' },
    { key: 'contactForm', varName: '--contact-form' },
    { key: 'processStep', varName: '--process-step' },
    { key: 'socialIcon', varName: '--social-icon' },
    { key: 'h1', varName: '--page-title' } // ????????
];

const legacyColorKeysAndLabels = [
    { key: 'heroTitle', varName: '--hero-h1-text-color' },
    { key: 'heroSubtitle', varName: '--hero-h2-text-color' },
    { key: 'heroParagraph', varName: '--hero-p1-text-color' },
    { key: 'h1', varName: '--section-h1-text-color' },
    { key: 'h2', varName: '--section-h2-text-color' },
    { key: 'h3', varName: '--section-h3-text-color' },
    { key: 'h4', varName: '--section-h4-text-color' },
    { key: 'p1', varName: '--section-p1-text-color' },
    { key: 'p2', varName: '--section-p2-text-color' },
    { key: 'projectTitle', varName: '--thumbnail-title-text-color' },
    { key: 'projectSubtitle', varName: '--thumbnail-subtitle-text-color' },
    { key: 'background', varName: '--background-fill-color' },
    { key: 'navigation', varName: '--hero-nav-button-text-color' },
    { key: 'navigationHover', varName: '--hero-nav-button-text-color-hover' },
    { key: 'navigationFill', varName: '--hero-nav-button-fill-color' },
    { key: 'navigationOutline', varName: '--hero-nav-button-outline-color' },
    { key: 'navigationFillHover', varName: '--hero-nav-button-fill-color-hover' },
    { key: 'navigationOutlineHover', varName: '--hero-nav-button-outline-color-hover' },
    { key: 'hyperLink', varName: '--link-color' },
    { key: 'dividerLine', varName: '--divider-line-fill-color' },
    { key: 'buttonFill', varName: '--cta-button-fill-color' },
    { key: 'buttonFillHover', varName: '--cta-button-fill-color-hover' },
    { key: 'buttonOutline', varName: '--cta-button-outline-color' },
    { key: 'buttonOutlineHover', varName: '--cta-button-outline-color-hover' },
    { key: 'buttonText', varName: '--cta-button-text-color' },
    { key: 'buttonTextHover', varName: '--cta-button-text-color-hover' },
    { key: 'secondaryButtonFill', varName: '--cta-secondary-button-fill-color' },
    { key: 'secondaryButtonFillHover', varName: '--cta-secondary-button-fill-color-hover' },
    { key: 'secondaryButtonOutline', varName: '--cta-secondary-button-outline-color' },
    { key: 'secondaryButtonOutlineHover', varName: '--cta-secondary-button-outline-color-hover' },
    { key: 'secondaryButtonText', varName: '--cta-secondary-button-text-color' },
    { key: 'secondaryButtonTextHover', varName: '--cta-secondary-button-text-color-hover' },
    { key: 'labelFill', varName: '--label-fill-color' },
    { key: 'labelOutline', varName: '--label-outline-color' },
    { key: 'labelText', varName: '--label-text-color' },
    { key: 'sidebarBackground', varName: '--sidebar-background-fill-color' },
    { key: 'cardFill', varName: '--card-fill-color' },
    { key: 'cardOutline', varName: '--card-outline-color' },
    { key: 'contactFormOutline', varName: '--contact-form-outline-color' },
    { key: 'contactFormText', varName: '--contact-form-text-color' },
    { key: 'processOutline', varName: '--process-step-outline-color' },
    { key: 'processText', varName: '--process-step-text-color' },
    { key: 'processFillActive', varName: '--process-step-fill-color-active' },
    { key: 'processTextActive', varName: '--process-step-text-color-active' },
    { key: 'socialIconText', varName: '--social-icon-text-color' },
    { key: 'socialIconFill', varName: '--social-icon-fill-color' },
    { key: 'socialIconOutline', varName: '--social-icon-outline-color' },
    { key: 'socialIconTextHover', varName: '--social-icon-text-color-hover' },
    { key: 'socialIconFillHover', varName: '--social-icon-fill-color-hover' },
    { key: 'socialIconOutlineHover', varName: '--social-icon-outline-color-hover' },
    { key: 'h1', varName: '--page-title-text-color' }
]

const heroColorKeysAndLabels= [
    { key: 'heroTitle', varName: '--portfolio-hero-h1' },
    { key: 'heroSubtitle', varName: '--portfolio-hero-h2' },
    { key: 'heroParagraph', varName: '--portfolio-hero-p1' },
    { key: 'navigation', varName: '--portfolio-hero-nav-button' },
    { key: 'hyperLink', varName: '--link' },
    { key: 'socialIcon', varName: '--portfolio-social-icon' },
    { key: 'background', varName: '--portfolio-background' },
]
const legacyHeroColorKeysAndLabels= [
    { key: 'heroTitle', varName: '--portfolio-hero-h1-text-color' },
    { key: 'heroSubtitle', varName: '--portfolio-hero-h2-text-color' },
    { key: 'heroParagraph', varName: '--portfolio-hero-p1-text-color' },
    { key: 'background', varName: '--portfolio-background-fill-color' },
    { key: 'navigation', varName: '--portfolio-hero-nav-button-text-color' },
    { key: 'navigationHover', varName: '--portfolio-hero-nav-button-text-color-hover' },
    { key: 'navigationFill', varName: '--portfolio-hero-nav-button-fill-color' },
    { key: 'navigationOutline', varName: '--portfolio-hero-nav-button-outline-color' },
    { key: 'navigationFillHover', varName: '--portfolio-hero-nav-button-fill-color-hover' },
    { key: 'navigationOutlineHover', varName: '--portfolio-hero-nav-button-outline-color-hover' },
    { key: 'hyperLink', varName: '--portfolio-hero-link-text-color' },
    { key: 'socialIconText', varName: '--portfolio-social-icon-text-color' },
    { key: 'socialIconFill', varName: '--portfolio-social-icon-fill-color' },
    { key: 'socialIconOutline', varName: '--portfolio-social-icon-outline-color' },
    { key: 'socialIconTextHover', varName: '--portfolio-social-icon-text-color-hover' },
    { key: 'socialIconFillHover', varName: '--portfolio-social-icon-fill-color-hover' },
    { key: 'socialIconOutlineHover', varName: '--portfolio-social-icon-outline-color-hover' },
]

function addStylesToCssVars (varNamePrefix, key, fontStyles, ctaStyles, labelStyle) {
    let cssVars = {};

    const style = fontStyles[key];

    // add extra stroke CSS vars for CTA buttons
    if (varNamePrefix === '--cta-button') {
        const primaryCta = ctaStyles?.primary;
        cssVars['--cta-stroke-strength'] = primaryCta && primaryCta.stroke && primaryCta.stroke.strength ? `${primaryCta.stroke.strength}px` : null;
    }
    if (varNamePrefix === '--cta-secondary-button') {
        const secondaryCta = ctaStyles?.secondary;
        cssVars['--cta-secondary-stroke-strength'] = secondaryCta && secondaryCta.stroke && secondaryCta.stroke.strength ? `${secondaryCta.stroke.strength}px` : null;
    }
    if (varNamePrefix === '--label') {
        const strokeStrength = labelStyle && labelStyle.stroke && labelStyle.stroke.strength ? `${labelStyle.stroke.strength}px` : null;
        cssVars['--label-stroke-strength'] = strokeStrength;
    }

    // text styles
    cssVars[`${varNamePrefix}-font`] = `'${style.font}'`;
    cssVars[`${varNamePrefix}-height`] = getCalculatedLineHeight(style);
    cssVars[`${varNamePrefix}-height-mobile`] = getCalculatedLineHeight(style, true);
    cssVars[`${varNamePrefix}-height-tablet`] = getCalculatedLineHeight(style, false, true);
    cssVars[`${varNamePrefix}-size`] = `${style.size}px`;
    cssVars[`${varNamePrefix}-size-mobile`] = `${getCalculatedMobileFontSize(style.size)}px`;
    cssVars[`${varNamePrefix}-size-tablet`] = `${getCalculatedTabletFontSize(style.size)}px`;
    cssVars[`${varNamePrefix}-style`] = style.fontStyle ? `${style.fontStyle}` : null;
    cssVars[`${varNamePrefix}-weight`] = `${style.fontWeight}`;

    return cssVars
}

function getCalculatedLineHeight(style, isMobile = false, isTablet = false) {
    const fontSize = isMobile ? getCalculatedMobileFontSize(style.size) : isTablet ? getCalculatedTabletFontSize(style.size) : style.size;
    const lineHeight = style.lineHeight;
    const relativeLineHeight = style.relativeLineHeight; // relative line height is the unitless line-height value
    let calculatedLineHeight;

    if ((!relativeLineHeight && !lineHeight) ||
        (lineHeight && lineHeight.toLowerCase === "auto") ||
        isTablet ||
        isMobile
    ) {
        // if the font is larger than 72px set the line height to a fixed 105%
        if(fontSize > 72) {
            return 1.05
        }
        calculatedLineHeight = Math.round( (0.015 * Math.pow(fontSize, 2) - 2.067 * fontSize + 175.991) ) / 100;
    } else {
        calculatedLineHeight = !!relativeLineHeight ? relativeLineHeight : Math.round(lineHeight / fontSize * 100) / 100;
    }

    if(lineHeight && lineHeight.toLowerCase === "auto") {
        return calculatedLineHeight < 1.05
            ? 1.05
            : calculatedLineHeight > 1.5
                ? 1.5
                : calculatedLineHeight;
    } else {
        return calculatedLineHeight
    }

}

function getCalculatedMobileFontSize(desktopFontSize) {
    if(desktopFontSize < 15) {
        return desktopFontSize
    }

    let mobileFontSize = 0.4 * desktopFontSize + 9.6;

    if(mobileFontSize < 12) {
        return 12
    }

    return Math.round(mobileFontSize * 100) / 100
}

function getCalculatedTabletFontSize(desktopFontSize) {
    if(desktopFontSize < 15) {
        return desktopFontSize
    }

    let mobileFontSize = 0.6 * desktopFontSize + 6.4;

    if(mobileFontSize < 12) {
        return 12
    }

    return Math.round(mobileFontSize * 100) / 100
}

export function getAllTextStyles(fontStyles, ctaStyles, labelStyle) {
    let vars = {}
    textStyleKeysAndVarNames.forEach(property => {
        const { varName, key } = property;
        vars = {
            ...vars,
            ...addStylesToCssVars(varName, key, fontStyles, ctaStyles, labelStyle)
        };
    })
    return vars
}

export function getPortfolioHeroTextStyles(fontStyles) {
    let vars = {}
    portfolioHeroTextStyleKeysAndVarNames.forEach(property => {
        const { varName, key } = property;
        vars = {
            ...vars,
            ...addStylesToCssVars(varName, key, fontStyles, null)
        }
    })
    return vars
}

export function getCssVarsFromColorStyles(
    colorStyles,
    colors,
    colorPalette,
    isPortfolioHeroInCaseStudy = false,
    customKeysAndLabels = null,
) {
    const colorMap = {};
    const params = new URLSearchParams(location.search);
    const isUsingNewColors = params.get("colors") === "useNew";

    const resolveLegacyColors = (keysAndLabels) => {
        keysAndLabels.forEach(({ key, varName }) => {
            const style = colorStyles[key];
            if (!style) {
                console.warn("legacy color resolver:")
                console.warn(`Style key "${key}" not found in color styles. Skipping color variable "${varName}".`);
                return;
            }

            colorMap[varName] = resolveSimpleColor(style);
        });
    };

    const resolveNewColors = (keysAndLabels) => {
        //console.log("reading new portfolio colors");
        const colorTypes = ["text", "fill", "outline"];
        const colorStates = ["idle", "hover", "active"];

        keysAndLabels.forEach(({ key, varName }) => {
            const style = colors[key];
            if (!style) {
                console.warn("new color resolver:")
                console.warn(`Style key "${key}" not found in color styles. Skipping color variable "${varName}".`);
                return;
            }

            colorTypes.forEach((type) => {
                const colorData = style[type];
                colorStates.forEach((state) => {
                    const stateData = colorData?.[state];
                    if (stateData) {
                        const resolved = resolveComplexColor(stateData);
                        if (resolved) {
                            const suffix = state !== "idle" ? `-${state}` : "";
                            colorMap[`${varName}-${type}-color${suffix}`] = resolved;
                        }
                    }
                });
            });
        });
    };

    const resolveSimpleColor = (style) => {
        return style.paletteColorIndex !== null && style.paletteColorIndex >= 0
            ? colorPalette.colors[style.paletteColorIndex]
            : style.color;
    };

    const resolveComplexColor = (colorData) => {
        return colorData.paletteColorIndex !== null && colorData.paletteColorIndex >= 0
            ? colorPalette.colors[colorData.paletteColorIndex]
            : colorData.color;
    };

    const readNewColors = isUsingNewColors || !colorStyles
    const keysAndLabels = customKeysAndLabels
        ?? (isPortfolioHeroInCaseStudy
                ? (readNewColors ? heroColorKeysAndLabels : legacyHeroColorKeysAndLabels)
                : (readNewColors ? colorKeysAndLabels : legacyColorKeysAndLabels)
        );

    if (readNewColors) {
        resolveNewColors(keysAndLabels);
    } else {
        resolveLegacyColors(keysAndLabels);
    }

    return colorMap;
}

export function getCssVarsFromColorPalette(colorPalette) {
    let vars = {}
    if(colorPalette.colors) {
        colorPalette.colors.forEach((color, index) => {
            vars[`--paletteColor${index+1}`] = color
        })
    }
    return vars
}

export function updateSectionLevelColorCssInHead(section, colorPalette = folioStore.getters["design/colorPalette"]) {
    if(!section || !section.designSettings?.colors) {
        return
    }

    let cssVars = {}
    const isNavigationSection = section.type === SectionTypesEnum.NAVIGATION;
    const isCardsSection = section.type === SectionTypesEnum.CARDS;
    const hasCardsModuleBackground = isCardsSection &&
        section.moduleBackgroundSettings?.moduleBackgroundType === "SOLID" &&
        !!section.moduleBackgroundSettings?.color;
    const hasSectionBackground = isNavigationSection
        ? !!section.moduleBackgroundSettings?.color
        : (
            (section.backgroundSettings?.selectedBackgroundStyleId !== null && section.backgroundSettings?.selectedBackgroundStyleId !== "0")
            || section.image?.url
            || hasCardsModuleBackground
        );

    if(
        section.designSettings?.colors && section.type !== SectionTypesEnum.LOGO_WALL &&
        hasSectionBackground
    ) {
        cssVars = {
            ...getCssVarsFromColorStyles(null, section.designSettings.colors, colorPalette)
        }
    }

    if (isNavigationSection) {
        const navBg = section.moduleBackgroundSettings?.color;
        cssVars["--background-fill-color"] = Number.isFinite(navBg?.paletteColorIndex)
            ? colorPalette?.colors?.[navBg.paletteColorIndex]
            : resolveNonHexColor(navBg?.color, colorPalette);
    } else if (isCardsSection && hasCardsModuleBackground) {
        const cardBg = section.moduleBackgroundSettings?.color;
        cssVars["--background-fill-color"] = Number.isFinite(cardBg?.paletteColorIndex)
            ? colorPalette?.colors?.[cardBg.paletteColorIndex]
            : resolveNonHexColor(cardBg?.color, colorPalette);
    } else {
        cssVars["--background-fill-color"] = resolveNonHexColor(section.backgroundSettings?.backgroundColor, colorPalette);
    }

    const isRelatedFooter = section.type === SectionTypesEnum.RELATED_CASE_STUDIES_FOOTER;
    const isFooter = section.type === SectionTypesEnum.FOOTER; // TODO: use section ID on footer
    const styleTagIdVar = isRelatedFooter
        ? 'related-case-studies-footer'
        : isFooter
            ? 'portfolio-footer'
            : section._id
                ? `section-${section._id}`
                : 'portfolio-hero';
    let cssSelector = isRelatedFooter
        ? '.related-case-studies'
        : isFooter
            ? '.portfolio-footer'
            : section._id
                ? `#section-${section._id}`
                : '.ufo--page-hero.home';

    injectCssVars(cssVars, `dynamic-css-vars-for-${styleTagIdVar}`, cssSelector);

    // if it is a portfolio hero fix the navigation color on mobile
    if(!section._id || section.type === SectionTypesEnum.MAIN_HEADER) {
        const colorKeysAndLabels = [
            {key: 'background', varName: '--background'},
            {key: 'navigation', varName: '--hero-nav-button'},
        ];

        // TODO update this cssSelector mayhem once the hero + nav refactoring is done
        injectCssVars(
            getCssVarsFromColorStyles(null, folioStore.getters["design/colors"], folioStore.getters["design/colorPalette"], false, colorKeysAndLabels),
            `dynamic-css-vars-for-portfolio-hero-mobile`,
            '.ufo--page-hero.home .navbar-container, .navbar-id.nav-open, .ufo--site-nav .navbar-container, .navbar-toggle .hamburger.close.nav-open',
            true
        );
    }
}

export function resetSectionLevelCustomColors(section, colorPalette = folioStore.getters["design/colorPalette"]) {
    if(!section) {
        return
    }

    if(!section.designSettings?.colors || section.foregroundColorSettings?.option === "custom") {
        return
    }

    const backgroundColors = {
        background: resolveNonHexColor( section.backgroundSettings?.backgroundColor, colorPalette) || resolveColor(section.moduleBackgroundSettings?.color, colorPalette),
        gradient1: resolveNonHexColor(section.backgroundSettings?.gradientColor1, colorPalette),
        gradient2: resolveNonHexColor(section.backgroundSettings?.gradientColor2, colorPalette)
    }

    // mix background colors if the bg style is gradient
    if(section.backgroundSettings?.selectedBackgroundStyleId !== null && section.backgroundSettings?.selectedBackgroundStyleId > 1) {
        backgroundColors.background = mixColors(backgroundColors.background, backgroundColors.gradient1);
    }

    const foregroundColors = JSON.parse(JSON.stringify( section.designSettings.colors ));

    const bestColor = getBestColor(backgroundColors.background, colorPalette.colors);

    const updatedSection = JSON.parse(JSON.stringify( section ));

    updatedSection.foregroundColorSettings = {
        ...updatedSection.foregroundColorSettings,
        color: bestColor.color,
        paletteColorIndex: bestColor.index,
        option: bestColor.type
    }
    updatedSection.designSettings.colors = {
        ...setAutoColorForSectionForegroundItems(
            foregroundColors,
            backgroundColors.background,
            colorPalette,
            folioStore.getters["design/colorGroupsWithDifferentRulesInSection"]()
        ),
    }

    if(section._id) {
        folioStore.commit("updateSection", updatedSection);
    } else {
        folioStore.commit("updatePortfolioHero", updatedSection);
    }

    updateSectionLevelColorCssInHead(updatedSection);

}